import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './mobile-stabilizer.css';
import { enforceRoleClaim, setEmailConfirmedFlag } from './utils/auth';
import { getMe } from './services/notificationService';
import RoleGuard from './components/RoleGuard';

import LandingPage from './components/LandingPage';
import AuthPortal from './components/authentication/AuthPortal';
import RegisterPortal from './components/authentication/RegisterPortal';
import Dashboard from './components/Dashboard/Dashboard';
import ATSScorePage from './components/ATSScore';
import ResumeBuilder from './components/resume-builder/ResumeBuilder';
import ResumeEditorPage from './components/resume-builder/ResumeEditorPage';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminTemplates from './components/admin/AdminTemplates';
import PuppeteerTemplateSelector from './components/ResumeBuilderPuppeteer/PuppeteerTemplateSelector';
import PuppeteerResumeEditor from './components/ResumeBuilderPuppeteer/PuppeteerResumeEditor';
import Profile from './components/Profile/Profile';
import PublicProfile from './components/Profile/PublicProfile';
import Settings from './components/Settings/Settings';
import MockInterviewDashboard from './pages/MockInterview/MockInterviewDashboard';
import BookInterviewPage from './pages/MockInterview/BookInterviewPage';
import AIInterviewPage from './pages/AIInterview/AIInterviewPage';
import AIInterviewHistory from './pages/AIInterview/AIInterviewHistory';
import InterviewerDashboard from './pages/MockInterview/InterviewerDashboard';
import AdminInterviewerPage from './pages/MockInterview/AdminInterviewerPage';
import MyFeedbackPage from './pages/MockInterview/MyFeedbackPage';
import MeetingRoomPage from './pages/Meeting/MeetingRoomPage';
import PaymentHistoryPage from './pages/Payment/PaymentHistoryPage';
import AdminPlansPage from './components/admin/AdminPlansPage';
import NotificationsPage from './components/Notifications/NotificationsPage';
import ForgotPasswordPage from './components/authentication/ForgotPasswordPage';
import VerifyEmailPage from './components/authentication/VerifyEmailPage';
import EmailConfirmedPage from './components/authentication/EmailConfirmedPage';
import AdminEmailTemplatesPage from './components/admin/AdminEmailTemplatesPage';
import AdminEmailLogsPage from './components/admin/AdminEmailLogsPage';

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  return token ? children : <Navigate to="/admin/login" replace />;
};


function App() {
  const [theme, setTheme] = useState('dark');
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  useEffect(() => {
    // Old tokens (pre role-claim deploy) get force-logged-out so users
    // re-login and pick up a JWT containing role claims.
    enforceRoleClaim();

    // Sync emailConfirmed from server — handles confirmations done in
    // another tab/device since the local user object was last written.
    if (!localStorage.getItem('token')) return;
    getMe()
      .then((me) => {
        if (me && typeof me.emailConfirmed === 'boolean') {
          setEmailConfirmedFlag(me.emailConfirmed);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Routes>

        {/* Public routes */}
        <Route path="/" element={<LandingPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/login" element={<AuthPortal theme={theme} />} />
        <Route path="/signup" element={<RegisterPortal theme={theme} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage theme={theme} />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
        <Route path="/public/profile/:token" element={<PublicProfile />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard theme={theme} toggleTheme={toggleTheme} />
          </ProtectedRoute>
        } />

        <Route path="/ats" element={
          <ProtectedRoute>
            <ATSScorePage theme={theme} toggleTheme={toggleTheme} />
          </ProtectedRoute>
        } />

        <Route path="/resume-builder" element={
          <ProtectedRoute>
            <ResumeBuilder theme={theme} toggleTheme={toggleTheme} />
          </ProtectedRoute>
        } />

        <Route path="/resume-builder/editor/:resumeId?" element={
          <ProtectedRoute>
            <ResumeEditorPage theme={theme} toggleTheme={toggleTheme} />
          </ProtectedRoute>
        } />

        {/* Puppeteer Resume Builder V2 */}
        <Route path="/resume-builder-v2" element={<ProtectedRoute><PuppeteerTemplateSelector theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/resume-builder-v2/editor/:templateId" element={<ProtectedRoute><PuppeteerResumeEditor theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />

        {/* Placeholder routes */}
        <Route path="/campus-to-job" element={<ProtectedRoute><div>Campus to Job — Coming Soon</div></ProtectedRoute>} />
        <Route path="/codeverse" element={<ProtectedRoute><div>Codeverse — Coming Soon</div></ProtectedRoute>} />
        <Route path="/mock-interview" element={
          <ProtectedRoute><MockInterviewDashboard theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/mock-interview/book" element={
          <ProtectedRoute><BookInterviewPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/mock-interview/ai" element={<Navigate to="/ai-interview" replace />} />
        <Route path="/ai-interview" element={
          <ProtectedRoute><AIInterviewPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/ai-interview/history" element={
          <ProtectedRoute><AIInterviewHistory theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/mock-interview/my-feedback" element={
          <ProtectedRoute><MyFeedbackPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/mock-interview/interviewer" element={
          <RoleGuard roles={["Interviewer"]}><InterviewerDashboard theme={theme} toggleTheme={toggleTheme} /></RoleGuard>
        } />
        <Route path="/mock-interview/admin" element={
          <RoleGuard roles={["Admin"]}><AdminInterviewerPage theme={theme} toggleTheme={toggleTheme} /></RoleGuard>
        } />
        <Route path="/meeting/:roomId" element={
          <ProtectedRoute><MeetingRoomPage /></ProtectedRoute>
        } />
        <Route path="/payment/history" element={
          <ProtectedRoute><PaymentHistoryPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />
        <Route path="/courses" element={<ProtectedRoute><div>Courses — Coming Soon</div></ProtectedRoute>} />
        <Route path="/notifications" element={
          <ProtectedRoute><NotificationsPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/templates" element={<AdminRoute><AdminTemplates /></AdminRoute>} />
        <Route path="/admin/plans" element={<AdminRoute><AdminPlansPage /></AdminRoute>} />
        <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplatesPage /></AdminRoute>} />
        <Route path="/admin/email-logs" element={<AdminRoute><AdminEmailLogsPage /></AdminRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </BrowserRouter>
  );
}

export default App;
